import { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import PriceListSelect from "../../../../components/common/price-list-select/PriceListSelect";
// Direct import: routes.tsx (in configs) imports this page, so the configs
// barrel may not be initialised yet when this module loads
import { Colors } from "../../../../configs/colors";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  clonePriceListAction,
  createPriceListAction,
  deletePriceListAction,
  getPriceListsAction,
  setDefaultPriceListAction,
  updatePriceListAction,
} from "../../../../redux/actions/priceListsAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { PriceListSummary } from "../../../../typings/interfaces/responses/price-list-summary-response";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: { fontWeight: "bold", fontSize: "var(--fs-xs)" },
  [`&.${tableCellClasses.body}`]: { fontSize: "var(--fs-2xs)" },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const actionButtonSx = { height: "2.2rem", fontSize: "var(--fs-2xs)", paddingInline: "0.9rem" };

type DialogState =
  | { kind: "create" }
  | { kind: "edit"; list: PriceListSummary }
  | { kind: "clone"; list: PriceListSummary }
  | { kind: "default"; list: PriceListSummary }
  | { kind: "delete"; list: PriceListSummary }
  | null;

/**
 * Named price lists. Customers are priced from their assigned list and fall
 * back to the default list for anything it doesn't price. Changing the
 * default reprices every customer without an assigned list.
 */
export const PriceLists: FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { priceLists, priceListsLoading } = useAppSelector((state) => state.priceLists);

  const [dialog, setDialog] = useState<DialogState>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [cloneFromId, setCloneFromId] = useState<number | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getPriceListsAction());
  }, []);

  const open = (next: DialogState) => {
    setName(next && next.kind === "edit" ? next.list.name : "");
    setCode(next && next.kind === "edit" ? next.list.code ?? "" : "");
    setCloneFromId(priceLists.find((l) => l.isDefault)?.id ?? null);
    setOverwrite(false);
    setDialog(next);
  };

  // Runs a list action, reports it, and reloads the counts
  const run = async (action: () => Promise<unknown>, success: string) => {
    setSaving(true);
    try {
      await action();
      snackbar.showSnackbar(success, "success");
      setDialog(null);
      dispatch(getPriceListsAction());
    } catch (err: any) {
      snackbar.showSnackbar(typeof err === "string" ? err : "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!dialog) return;
    switch (dialog.kind) {
      case "create":
        return run(
          () => dispatch(createPriceListAction({ name: name.trim(), code: code.trim() })).unwrap(),
          `Price list "${name.trim()}" created. Upload or clone prices into it next.`
        );
      case "edit":
        return run(
          () =>
            dispatch(
              updatePriceListAction({ id: dialog.list.id, name: name.trim(), code: code.trim() })
            ).unwrap(),
          "Price list updated."
        );
      case "clone":
        return run(
          () =>
            dispatch(
              clonePriceListAction({ id: dialog.list.id, fromId: cloneFromId!, overwrite })
            ).unwrap(),
          `Prices copied into "${dialog.list.name}".`
        );
      case "default":
        return run(
          () => dispatch(setDefaultPriceListAction(dialog.list.id)).unwrap(),
          `"${dialog.list.name}" is now the default price list.`
        );
      case "delete":
        return run(
          () => dispatch(deletePriceListAction(dialog.list.id)).unwrap(),
          `"${dialog.list.name}" deleted.`
        );
    }
  };

  const defaultList = priceLists.find((l) => l.isDefault);
  const cancelButtonSx = { height: "2.4rem", backgroundColor: "#e0e0e0", color: Colors.TEXT_GRAY_DARK };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
              Price Lists
            </Typography>
            <Typography sx={{ color: Colors.TEXT_GRAY_DARK, opacity: 0.8, fontSize: "var(--fs-2xs)" }}>
              Customers use their assigned list; anything it doesn't price comes from the default
              {defaultList ? ` (${defaultList.name})` : ""}.
            </Typography>
          </Box>
          <CustomButton sx={{ width: "11rem", height: "3rem" }} onClick={() => open({ kind: "create" })}>
            Add New
          </CustomButton>
        </Box>

        <Table sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Code</StyledTableCell>
              <StyledTableCell align="right">Prices</StyledTableCell>
              <StyledTableCell align="right">Customers</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceListsLoading && priceLists.length === 0 && (
              <StyledTableRow>
                <StyledTableCell colSpan={6} sx={{ textAlign: "center" }}>
                  <CircularProgress size={24} />
                </StyledTableCell>
              </StyledTableRow>
            )}
            {priceLists.map((list) => (
              <StyledTableRow key={list.id}>
                <StyledTableCell sx={{ fontWeight: 600 }}>{list.name}</StyledTableCell>
                <StyledTableCell>{list.code || "-"}</StyledTableCell>
                <StyledTableCell align="right">{list.priceCount.toLocaleString()}</StyledTableCell>
                <StyledTableCell align="right">
                  {list.isDefault ? "All others" : list.customerCount.toLocaleString()}
                </StyledTableCell>
                <StyledTableCell>
                  {list.isDefault ? (
                    <Box
                      component="span"
                      sx={{
                        px: "0.6rem",
                        py: "0.2rem",
                        borderRadius: "0.4rem",
                        backgroundColor: "#e3f4ec",
                        color: Colors.SECONDARY,
                        fontWeight: 600,
                      }}
                    >
                      Default
                    </Box>
                  ) : list.active ? (
                    "Active"
                  ) : (
                    "Inactive"
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <CustomButton sx={actionButtonSx} onClick={() => open({ kind: "edit", list })}>
                      Rename
                    </CustomButton>
                    <CustomButton sx={actionButtonSx} onClick={() => open({ kind: "clone", list })}>
                      Copy prices from…
                    </CustomButton>
                    {!list.isDefault && (
                      <CustomButton sx={actionButtonSx} onClick={() => open({ kind: "default", list })}>
                        Set as default
                      </CustomButton>
                    )}
                    {!list.isDefault && (
                      <CustomButton
                        sx={{ ...actionButtonSx, backgroundColor: "#e53935" }}
                        onClick={() => open({ kind: "delete", list })}
                      >
                        Delete
                      </CustomButton>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>

        {/* Create / rename */}
        <Dialog
          open={dialog?.kind === "create" || dialog?.kind === "edit"}
          onClose={() => setDialog(null)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "26rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            {dialog?.kind === "edit" ? "Rename Price List" : "New Price List"}
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: "0.5rem !important" }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. 2026 Standard"
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Code (optional)"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              fullWidth
              size="small"
              placeholder="e.g. S26"
              inputProps={{ maxLength: 20 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton onClick={() => setDialog(null)} sx={cancelButtonSx}>
              Cancel
            </CustomButton>
            <CustomButton onClick={handleSave} disabled={!name.trim() || saving} sx={{ height: "2.4rem" }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </CustomButton>
          </DialogActions>
        </Dialog>

        {/* Clone */}
        <Dialog
          open={dialog?.kind === "clone"}
          onClose={() => setDialog(null)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "28rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Copy prices into {dialog?.kind === "clone" ? dialog.list.name : ""}
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: "0.5rem !important" }}>
            <PriceListSelect value={cloneFromId} onChange={setCloneFromId} label="Copy from" fullWidth />
            {dialog?.kind === "clone" && dialog.list.priceCount > 0 && (
              <FormControlLabel
                control={<Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />}
                label={
                  <Typography sx={{ fontSize: "var(--fs-2xs)" }}>
                    Replace its {dialog.list.priceCount.toLocaleString()} existing prices where they overlap
                  </Typography>
                }
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton onClick={() => setDialog(null)} sx={cancelButtonSx}>
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSave}
              disabled={
                saving ||
                cloneFromId == null ||
                (dialog?.kind === "clone" && (cloneFromId === dialog.list.id || (dialog.list.priceCount > 0 && !overwrite)))
              }
              sx={{ height: "2.4rem" }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : "Copy"}
            </CustomButton>
          </DialogActions>
        </Dialog>

        {/* Set default / delete confirmations */}
        <Dialog
          open={dialog?.kind === "default" || dialog?.kind === "delete"}
          onClose={() => setDialog(null)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "26rem", maxWidth: "34rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            {dialog?.kind === "default" ? "Change the default price list?" : "Delete price list?"}
          </DialogTitle>
          <DialogContent>
            {dialog?.kind === "default" && (
              <Typography sx={{ fontSize: "var(--fs-2xs)" }}>
                Every customer without an assigned list will be charged from{" "}
                <strong>{dialog.list.name}</strong> ({dialog.list.priceCount.toLocaleString()} prices) instead
                of <strong>{defaultList?.name}</strong>. Existing invoices keep their prices. To undo, set{" "}
                {defaultList?.name} as the default again.
              </Typography>
            )}
            {dialog?.kind === "delete" && (
              <Typography sx={{ fontSize: "var(--fs-2xs)" }}>
                <strong>{dialog.list.name}</strong> and its {dialog.list.priceCount.toLocaleString()} prices
                will be removed. Existing invoices keep their prices.
                {dialog.list.customerCount > 0 &&
                  ` It is assigned to ${dialog.list.customerCount} customer(s); reassign them first.`}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton onClick={() => setDialog(null)} sx={cancelButtonSx}>
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSave}
              disabled={saving || (dialog?.kind === "delete" && dialog.list.customerCount > 0)}
              sx={{ height: "2.4rem", ...(dialog?.kind === "delete" ? { backgroundColor: "#e53935" } : {}) }}
            >
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : dialog?.kind === "default" ? (
                "Make default"
              ) : (
                "Delete"
              )}
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default PriceLists;
